import React, { useId, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox,
  Chip, FormControlLabel, FormLabel, Link, Paper, Radio, RadioGroup, Stack,
  TextField, Typography,
} from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssistantMarkdown from '../FarChat/AssistantMarkdown';
import ClarificationCard from '../FarChat/ClarificationCard';
import { stageFormGuidance } from '../HomeChat/stageFormGuidance';
import type { ChatExample, ExampleEntry, ExampleField } from './types';

const displayValue = (value: unknown): string => value === null || value === undefined
  ? '' : typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
const missingValue = (value: unknown) => value === null || value === undefined
  || (typeof value === 'string' && value.trim() === '')
  || (Array.isArray(value) && value.length === 0);
function hasSavedValue(value: unknown): boolean {
  if (missingValue(value)) return false;
  if (Array.isArray(value)) return value.some(hasSavedValue);
  if (typeof value === 'object' && value) return Object.values(value).some(hasSavedValue);
  return true;
}

export function safeExampleLink(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:'
      && (parsed.hostname === 'acquisition.gov' || parsed.hostname.endsWith('.acquisition.gov'))
      && !parsed.username && !parsed.password ? url : undefined;
  } catch { return undefined; }
}

// The same role alignment, colors and spacing used by the MR/GPC chat panels.
const bubbleSx = (role: 'user' | 'assistant') => ({
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  maxWidth: '86%',
  px: 1.5,
  py: 1,
  borderRadius: 2,
  bgcolor: role === 'user' ? 'primary.main' : 'action.hover',
  color: role === 'user' ? 'primary.contrastText' : 'text.primary',
  overflowWrap: 'anywhere',
  minWidth: 0,
});

// Locked choices retain their live colors instead of making saved answers faint.
const lockedControlSx = {
  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'inherit', color: 'text.primary' },
  '& .MuiFormControlLabel-label.Mui-disabled': { color: 'text.primary' },
  '& .MuiCheckbox-root.Mui-disabled, & .MuiRadio-root.Mui-disabled': { color: 'text.secondary' },
  '& .MuiCheckbox-root.Mui-checked.Mui-disabled, & .MuiRadio-root.Mui-checked.Mui-disabled': { color: 'primary.main' },
};

// Native date inputs require ISO. Preserve unknown saved text instead of dropping it.
function savedDate(value: string): { value: string; type: 'date' | 'text' } {
  if (!value) return { value, type: 'date' };
  const candidate = /^\d{8}$/.test(value)
    ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
    : /^\d{2}\/\d{2}\/\d{4}$/.test(value)
      ? `${value.slice(6, 10)}-${value.slice(0, 2)}-${value.slice(3, 5)}` : value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
    const [year, month, day] = candidate.split('-').map(Number);
    const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= days[month - 1]) return { value: candidate, type: 'date' };
  }
  return { value, type: 'text' };
}

function SavedField({ field, id }: { field: ExampleField; id: string }) {
  const value = displayValue(field.value);
  const label = `${field.label}${field.required ? ' *' : ''}`;
  const type = field.input_type;
  const implicitChoice = !field.options?.length && (type === 'yes_no' || type === 'boolean_choice');
  const options = implicitChoice
    ? [{ value: type === 'yes_no' ? 'Yes' : 'true', label: 'Yes' }, { value: type === 'yes_no' ? 'No' : 'false', label: 'No' }]
    : field.options || [];
  const choiceValue = implicitChoice && (field.value === true || field.value === 'Yes') ? options[0].value
    : implicitChoice && (field.value === false || field.value === 'No') ? options[1].value : value;
  let control: React.ReactNode;
  if (type === 'group') {
    const values = field.value && typeof field.value === 'object' && !Array.isArray(field.value)
      ? field.value as Record<string, unknown> : {};
    control = <Stack spacing={1.5}>{field.item_fields?.map(column => (
      <SavedField key={column.key} id={`${id}-${column.key}`} field={{ ...column, value: values[column.key] }} />
    ))}</Stack>;
  } else if (type === 'line_items') {
    const rows = Array.isArray(field.value) ? field.value : [];
    control = <Stack spacing={1}>{rows.map((row, index) => (
      <Paper key={index} variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">Item {index + 1}</Typography>
        <Stack spacing={1.5} sx={{ mt: 1 }}>{field.item_fields?.map(column => (
          <SavedField key={column.key} id={`${id}-${index}-${column.key}`} field={{ ...column, value: row[column.key] }} />
        ))}</Stack>
      </Paper>
    ))}</Stack>;
  } else if (type === 'file' || type === 'files') {
    const files = Array.isArray(field.value) ? field.value.map(String) : value ? [value] : [];
    control = <Stack spacing={1} alignItems="flex-start">
      <Button disabled variant="outlined" startIcon={<AttachFileOutlinedIcon />}>Attach document</Button>
      {files.map((filename, index) => <Chip key={`${filename}-${index}`} icon={<AttachFileOutlinedIcon />} label={filename} variant="outlined" sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5 } }} />)}
      {!files.length && <Typography variant="caption" color="text.secondary">Not provided</Typography>}
    </Stack>;
  } else if (['radio', 'yes_no', 'boolean_choice', 'checkboxes', 'multi_select'].includes(type)) {
    const multiple = ['checkboxes', 'multi_select'].includes(type);
    control = multiple ? <Box role="group" aria-label={label}>{options.map(option => (
      <FormControlLabel key={option.value} label={option.label} control={<Checkbox disabled checked={Array.isArray(field.value) && field.value.some(item => String(item) === option.value)} inputProps={{ readOnly: true }} />} />
    ))}</Box> : <RadioGroup aria-label={label} value={choiceValue}>{options.map(option => (
      <FormControlLabel key={option.value} value={option.value} label={option.label} control={<Radio disabled inputProps={{ readOnly: true }} />} />
    ))}</RadioGroup>;
  } else if (type === 'boolean') {
    control = <Checkbox id={id} disabled checked={field.value === true} inputProps={{ readOnly: true, 'aria-label': label }} sx={{ p: 0.5 }} />;
  } else if (type === 'select') {
    control = <>
      <TextField id={id} select fullWidth size="small" value={value} disabled SelectProps={{ native: true }} inputProps={{ 'aria-label': label }}>
        {value === '' && !options.some(option => option.value === '') && <option value="">Not provided</option>}
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </TextField>
      <Typography variant="caption" color="text.secondary">Available choices: {options.map(option => option.label).join(' · ')}</Typography>
    </>;
  } else {
    const multiline = type === 'textarea' || type === 'json';
    const presentation = type === 'date' || type === 'date_yyyymmdd' ? savedDate(value)
      : { value, type: ['number', 'email', 'url'].includes(type) ? type : 'text' };
    control = <TextField id={id} fullWidth size="small" value={presentation.value} multiline={multiline}
      minRows={multiline ? 3 : undefined} maxRows={multiline ? 8 : undefined}
      type={presentation.type}
      InputProps={{ readOnly: true }} inputProps={{ 'aria-label': label, 'aria-required': field.required }} />;
  }
  return <Box sx={{ minWidth: 0, ...lockedControlSx }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
      <FormLabel component="label" htmlFor={id} sx={{ color: 'text.primary', fontWeight: 600 }}>{label}</FormLabel>
      {field.prefilled && <Chip label="Prefilled" size="small" />}
    </Stack>
    {field.help_text && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{field.help_text}</Typography>}
    {control}
    {missingValue(field.value) && !['file', 'files'].includes(type) && <Typography variant="caption" color="text.secondary">Not provided</Typography>}
  </Box>;
}

function SavedData({ value }: { value: unknown }) {
  if (missingValue(value)) return <Typography color="text.secondary">Not provided</Typography>;
  if (Array.isArray(value)) return <Stack component="ol" spacing={1.5} sx={{ pl: 2.5, my: 1 }}>{value.map((item, index) => <Box component="li" key={index}><SavedData value={item} /></Box>)}</Stack>;
  if (typeof value === 'object' && value) return <Box component="dl" sx={{ m: 0 }}>{Object.entries(value).map(([key, item]) => {
    const label = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    return <Box key={key} sx={{ my: 1.5, minWidth: 0 }}>
      <Typography component="dt" variant="subtitle2">{label.charAt(0).toUpperCase() + label.slice(1)}</Typography>
      <Box component="dd" sx={{ m: 0, mt: 0.5 }}><SavedData value={item} /></Box>
    </Box>;
  })}</Box>;
  return typeof value === 'string' ? <AssistantMarkdown text={value} suppressRemoteImages />
    : <Typography>{typeof value === 'boolean' ? value ? 'Yes' : 'No' : String(value)}</Typography>;
}

type FormEntry = Extract<ExampleEntry, { kind: 'form' }>;
function SavedForm({ entry }: { entry: FormEntry }) {
  // Snapshot provenance is explained once above the conversation. Keep only
  // guidance useful for answering this particular form beside its controls.
  const provenanceOnly = [
    'Read-only sample values from the corresponding workflow example.',
    'Curated read-only start confirmation using the saved requirement. This illustrates the Chat entry screen, not a historical chat click record.',
    'Curated read-only generation review using the saved requirement and source-plan confirmation. The retained generated SOW follows; this does not assert a historical chat approval click.',
  ];
  const instructions = provenanceOnly.includes(entry.instructions || '') ? undefined : entry.instructions;
  const guidance = stageFormGuidance(entry.prompt, instructions).filter(copy => copy !== entry.title);
  return <Paper variant="outlined" role="group" aria-label={entry.title} sx={{ borderRadius: 2, p: { xs: 1.5, sm: 2 }, bgcolor: 'background.paper', minWidth: 0 }}>
    <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{entry.title}</Typography>
    {guidance.map(copy => <Typography key={copy} variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>{copy}</Typography>)}
    <Stack spacing={2}>{entry.fields.map(field => <SavedField field={field} id={`${entry.entry_id}-${field.key}`} key={field.key} />)}</Stack>
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>{(entry.actions || []).map(label => (
      <Button disabled key={label} variant="contained" aria-label={`${label} (example action)`}>{label}</Button>
    ))}</Stack>
  </Paper>;
}

const rowFields = (field: ExampleField, row: unknown) => (field.item_fields || []).map(column => ({
  ...column, value: row && typeof row === 'object' ? (row as Record<string, unknown>)[column.key] : undefined,
}));
function hasAnsweredField(field: ExampleField): boolean {
  if (field.input_type === 'group' && field.item_fields?.length) return rowFields(field, field.value).some(hasAnsweredField);
  if (field.input_type === 'line_items' && field.item_fields?.length) return Array.isArray(field.value) && field.value.some(row => rowFields(field, row).some(hasAnsweredField));
  return hasSavedValue(field.value);
}
function ResponseValue({ value }: { value: unknown }) {
  if (!hasSavedValue(value)) return null;
  if (Array.isArray(value)) return <Box component="ul" sx={{ my: 0, pl: 2.5 }}>{value.filter(hasSavedValue).map((item, index) => <Box component="li" key={index}><ResponseValue value={item} /></Box>)}</Box>;
  if (typeof value === 'object' && value) return <Box component="ul" sx={{ my: 0, pl: 2.5 }}>{Object.entries(value).filter(([, item]) => hasSavedValue(item)).map(([key, item]) => <Box component="li" key={key}><ResponseValue value={item} /></Box>)}</Box>;
  return <Typography sx={{ whiteSpace: 'pre-wrap' }}>{typeof value === 'boolean' ? value ? 'Yes' : 'No' : String(value)}</Typography>;
}
function ResponseFields({ fields, numbered = false }: { fields: ExampleField[]; numbered?: boolean }) {
  return <Box component={numbered ? 'ol' : 'div'} sx={{ m: 0, ...(numbered ? { pl: 3 } : {}) }}>{fields.map((field, index) => hasAnsweredField(field) ? <Box component={numbered ? 'li' : 'div'} value={numbered ? index + 1 : undefined} key={field.key} sx={{ '& + &': { mt: 1.5 }, minWidth: 0 }}>
    {field.input_type === 'group' && field.item_fields?.length
      ? <ResponseFields fields={rowFields(field, field.value)} numbered={numbered} />
      : field.input_type === 'line_items' && field.item_fields?.length && Array.isArray(field.value)
        ? <Box component={numbered ? 'ol' : 'div'} sx={{ m: 0, ...(numbered ? { pl: 3 } : {}) }}>{field.value.map((row, index) => {
          const fields = rowFields(field, row);
          return fields.some(hasAnsweredField) ? <Box component={numbered ? 'li' : 'div'} value={numbered ? index + 1 : undefined} key={index} sx={{ '& + &': { mt: 1 } }}><ResponseFields fields={fields} /></Box> : null;
        })}</Box>
        : <ResponseValue value={Array.isArray(field.value)
          ? field.value.map(item => field.options?.find(option => option.value === String(item))?.label || item)
          : field.options?.find(option => option.value === String(field.value))?.label ?? field.value} />}
  </Box> : null)}</Box>;
}
function SavedResponse({ entry }: { entry: FormEntry | Extract<ExampleEntry, { kind: 'clarification' }> }) {
  return <Box data-example-response-for={entry.entry_id} data-message-role="user" aria-label={`Recorded answers: ${entry.kind === 'form' ? entry.title : 'Question clarification'}`} sx={bubbleSx('user')}>
    {entry.kind === 'form' ? <ResponseFields fields={entry.fields} numbered={entry.question_form === true} /> : <>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{entry.selected_question}</Typography>
      <Typography variant="caption" component="div" sx={{ color: 'inherit', mt: 0.5 }}>{entry.regime_options.find(option => option.value === entry.selected_regime)?.label || entry.selected_regime}</Typography>
    </>}
  </Box>;
}

function SavedResult({ entry, example }: { entry: Extract<ExampleEntry, { kind: 'result' }>; example: ChatExample }) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  return <>
    {(entry.data !== undefined || entry.content) && <Accordion disableGutters elevation={0} expanded={expanded} onChange={(_event, next) => setExpanded(next)} TransitionProps={{ unmountOnExit: true }} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' }, '&.Mui-expanded': { m: 0 } }}>
      <AccordionSummary component="button" type="button" id={`${id}-toggle`} aria-controls={`${id}-content`} expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 40, width: '100%', textAlign: 'left', flexDirection: 'row-reverse', gap: 1, '& .MuiAccordionSummary-expandIconWrapper': { mr: 0 } }}><Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>{entry.title}</Typography></AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>{entry.content && <AssistantMarkdown text={entry.content} suppressRemoteImages />}{entry.data !== undefined && <SavedData value={entry.data} />}</AccordionDetails>
    </Accordion>}
    {entry.data === undefined && !entry.content && <Typography component="h3" variant="subtitle1" fontWeight={600}>{entry.title}</Typography>}
    {entry.assets?.map(asset => {
      const prefix = `/api/chat-examples/${encodeURIComponent(example.example_id)}/assets/${encodeURIComponent(asset.asset_id)}?revision=${encodeURIComponent(example.revision)}`;
      return <Box key={asset.asset_id} sx={{ mt: 1 }}>{asset.available && asset.url === prefix
        ? <Link href={`${process.env.REACT_APP_API_URL || ''}${prefix}`} target="_blank" rel="noopener noreferrer">{asset.title} ({asset.filename})</Link>
        : <Typography>{asset.title} ({asset.filename}) · Unavailable</Typography>}</Box>;
    })}
  </>;
}

function Entry({ entry, example }: { entry: ExampleEntry; example: ChatExample }) {
  switch (entry.kind) {
    case 'message': return entry.role === 'assistant' ? <AssistantMarkdown text={entry.content} suppressRemoteImages /> : <Typography sx={{ whiteSpace: 'pre-wrap' }}>{entry.content}</Typography>;
    case 'form': return <SavedForm entry={entry} />;
    case 'clarification': return <ClarificationCard originalQuery={entry.original_query} enhancements={entry.enhancements}
      readOnlySelection={{ question: entry.selected_question, regime: entry.selected_regime, refinement: entry.refinement, versionOptions: entry.regime_options }} onResolve={() => undefined} />;
    case 'topic_focus': return <><Typography sx={{ fontWeight: 600 }}>{entry.prompt}</Typography><SavedField id={`${entry.entry_id}-focus`} field={{ key: 'focus', label: 'Topic focus', input_type: 'radio', value: entry.selected_option, options: entry.options.map(option => ({ value: option, label: option })) }} />{entry.refinement && <SavedField id={`${entry.entry_id}-refinement`} field={{ key: 'refinement', label: 'Topic refinement', input_type: 'textarea', value: entry.refinement }} />}<Button disabled variant="contained">Confirm</Button></>;
    case 'answer': return <><AssistantMarkdown text={entry.summary} suppressRemoteImages /><Box component="details" sx={{ mt: 1 }}><Typography component="summary" sx={{ cursor: 'pointer', fontWeight: 600 }}>Answer details</Typography><AssistantMarkdown text={entry.detail} suppressRemoteImages /></Box>{entry.citations.map(citation => <Box key={citation.id} sx={{ mt: 1 }}>{safeExampleLink(citation.url) ? <Link href={safeExampleLink(citation.url)} target="_blank" rel="noopener noreferrer">{citation.canonical || citation.section || citation.quote}</Link> : <Typography>{citation.canonical || citation.section || citation.quote}</Typography>}</Box>)}</>;
    case 'result': return <SavedResult entry={entry} example={example} />;
  }
}

export default function ChatExampleView({ example }: { example: ChatExample }) {
  return <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
    <Typography variant="h4" component="h2">{example.title}</Typography>
    <Alert severity="info" sx={{ my: 2 }}>Read-only chat example — this illustrated conversation shows saved fields, answers, and results.</Alert>
    <Typography variant="caption" color="text.secondary">Saved illustration · {example.content_date} · Revision {example.revision}</Typography>
    <Box role="log" aria-live="off" aria-label={`${example.title} conversation`} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2, maxWidth: 960, mx: 'auto', minWidth: 0 }}>
      {example.entries.map(entry => {
        const role = entry.kind === 'message' ? entry.role : 'assistant';
        const standalone = entry.kind === 'form' || entry.kind === 'clarification';
        const hasResponse = entry.kind === 'clarification' || (entry.kind === 'form' && entry.fields.some(hasAnsweredField));
        return <React.Fragment key={entry.entry_id}>
          <Box component="section" data-example-entry={entry.entry_id} data-message-role={role}
            sx={standalone ? { alignSelf: 'flex-start', width: '100%', maxWidth: entry.kind === 'clarification' ? '100%' : { xs: '100%', sm: '92%' }, minWidth: 0 } : bubbleSx(role)}>
            <Entry entry={entry} example={example} />
          </Box>
          {hasResponse && (entry.kind === 'form' || entry.kind === 'clarification') && <SavedResponse entry={entry} />}
        </React.Fragment>;
      })}
    </Box>
  </Box>;
}
